import {
  Copy,
  MagnifyingGlass,
  PenNib,
  Download,
  Function,
  Gear,
  Plus,
  X,
} from "@phosphor-icons/react"
import { Chart } from "@tokenterminal/core/feature/chart/Chart"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Checkbox } from "@tokenterminal/core/ui/input/checkbox/Checkbox"
import { MenuButton } from "@tokenterminal/core/ui/menu/MenuButton"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { LoadingSpinner } from "@tokenterminal/core/ui/progress/LoadingSpinner"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type CustomChartSerie } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { type ProjectDetailsMeta } from "@tokenterminal/tt-types"
import { withChartContext } from "@tokenterminal/ui/Chart/ChartContext"
import { COLORS_LARGE_SET } from "@tokenterminal/ui/Chart/colors"
import { type GranularityType } from "@tokenterminal/ui/Chart/utils/groupDataBasedOnGranularity"
import { colorsV4 } from "@tokenterminal/ui/theme/themeV4/colorsContract.css"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import { useProductsList } from "hooks/useProductsList"
import { atom } from "jotai"
import { LazyChartFormulaModal } from "modules/chart/ChartFormulaModal/LazyChartFormulaModal"
import { convertApiToZoom } from "modules/chart/Zoom/convertZoomToAPI"
import { type CustomChartConfigPayload } from "modules/chart-builder/types"
import { LazyProPaywallModal } from "modules/paywall/LazyProPaywallModal"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { type ReactNode, useCallback, useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { type MetricConfiguration } from "utils/trpc"
import { CopyChartAsImageModal } from "../../../components/CopyChartAsImageModal/CopyChartAsImageModal"
import { useAvailableEcosystemMetricIds } from "../hooks/useAvailableEcosystemMetricIds"
import { useAvailableMetricIdsForProject } from "../hooks/useAvailableMetricIdsForProject"
import { useMetricBreakdown } from "../hooks/useMetricBreakdowns"
import { useMetricsConfigurationList } from "../hooks/useMetricsConfigurationList"
import { useStudioLink } from "../hooks/useStudioLink"
import { MetricsDropdown } from "../MetricsDropdown"
import { type Comparison } from "../MetricsTab/ComparisonToolbar"
import GranularitySelectField from "../MetricsTab/GranularitySelectField"
import IntervalSelectField from "../MetricsTab/IntervalSelectField"
import useChartDownloadAction from "../MetricsTab/useChartDownloadAction"
import ComparisonButton from "./ComparisonButton"
import useMetricsConfigurationBySlug from "./useMetricsConfigurationBySlug"

const useChartConfig = ({
  metric2Slug,
  comparison,
  metric1Name,
  metric1Slug,
  data_id,
  isEcosystemMetric,
  interval,
  metric2Name,
  chartName,
  granularity,
  showCurrentPeriod,
  productId,
  metric1Configuration,
}: {
  metric2Slug: string | null
  comparison: Comparison | null
  metric1Name: string
  metric1Slug: string
  data_id: string
  isEcosystemMetric: (metricSlug: string) => boolean
  interval: string
  metric2Name: string
  chartName: string
  granularity: string
  showCurrentPeriod: boolean
  productId?: string
  metric1Configuration: MetricConfiguration
}) => {
  const result: CustomChartConfigPayload & {
    id: string
  } = useMemo(() => {
    let configs: CustomChartSerie[] = []
    if (!metric2Slug && !comparison) {
      configs = [
        {
          id: "metric1",
          yAxis: "left",
          title: metric1Name,
          colors: [],
          metric: metric1Slug,
          display: null,
          filters: [
            {
              type: "project",
              values: [data_id],
              comparator: "in",
            },
          ],
          groupBy: isEcosystemMetric(metric1Slug) ? "chain" : "project",
          projects: [],
          chart_type:
            interval === "24h"
              ? "bar-unstacked"
              : metric1Configuration.static
                ? "line"
                : "bar-stacked",
        },
      ]
      if (productId) {
        configs[0].filters.push({
          type: "product" as const,
          values: [productId],
          comparator: "in",
        })
      }
    } else if (comparison && comparison.type === "project") {
      configs = [
        {
          id: "project1",
          yAxis: "left",
          title: metric1Name,
          colors: [COLORS_LARGE_SET[0]],
          metric: metric1Slug,
          display: null,
          filters: [
            {
              type: "project",
              values: [data_id],
              comparator: "in",
            },
          ],
          groupBy: isEcosystemMetric(metric1Slug) ? "chain" : "project",
          projects: [],
          chart_type: interval === "24h" ? "bar-unstacked" : "line",
        },
        {
          id: "project2",
          yAxis: "left",
          title: metric1Name,
          colors: [COLORS_LARGE_SET[1]],
          metric: metric1Slug,
          display: null,
          filters: [
            {
              type: "project",
              values: [comparison.data_id],
              comparator: "in",
            },
          ],
          groupBy: isEcosystemMetric(metric1Slug) ? "chain" : "project",
          projects: [],
          chart_type: interval === "24h" ? "bar-unstacked" : "line",
        },
      ]
    } else if (comparison && comparison.type === "market_sector") {
      configs = [
        {
          id: "metric1",
          yAxis: "left",
          title: metric1Name,
          colors: [],
          metric: metric1Slug,
          display: null,
          filters: [
            {
              type: "project",
              values: [data_id],
              comparator: "in",
            },
          ],
          groupBy: "project",
          projects: [],
          chart_type: interval === "24h" ? "bar-stacked" : "area-stacked",
        },
        {
          id: "metric2",
          yAxis: "left",
          title: "Market sector",
          colors: ["#808080"],
          metric: metric1Slug,
          display: null,
          filters: [
            {
              type: "market_sector",
              values: [comparison.id],
              comparator: "in",
            },
          ],
          groupBy: null,
          projects: [],
          chart_type: interval === "24h" ? "bar-stacked" : "area-stacked",
        },
      ]
    } else if (metric2Slug) {
      configs = [
        {
          id: "metric1",
          yAxis: "left",
          title: metric1Name,
          colors: [],
          metric: metric1Slug,
          display: null,
          filters: [
            {
              type: "project",
              values: [data_id],
              comparator: "in",
            },
          ],
          groupBy: isEcosystemMetric(metric1Slug) ? "chain" : "project",
          projects: [],
          chart_type: interval === "24h" ? "bar-unstacked" : "line",
        },
        {
          id: "metric2",
          yAxis: "right",
          title: metric2Name,
          colors: [],
          metric: metric2Slug,
          display: null,
          filters: [
            {
              type: "project",
              values: [data_id],
              comparator: "in",
            },
          ],
          groupBy: isEcosystemMetric(metric2Slug) ? "chain" : "project",
          projects: [],
          chart_type: interval === "24h" ? "bar-unstacked" : "line",
        },
      ]
    }

    return {
      id: "some string",
      zoom: interval as Interval,
      title: chartName,
      shared: true,
      configs,
      show_table: false,
      show_title: false,
      description: null,
      granularity: granularity as GranularityType,
      show_description: false,
      // Since data will be incomplete for current period, we hide it
      show_current_period: showCurrentPeriod,
    }
  }, [
    metric2Slug,
    comparison,
    interval,
    chartName,
    granularity,
    showCurrentPeriod,
    metric1Name,
    metric1Slug,
    data_id,
    isEcosystemMetric,
    metric2Name,
    productId,
  ])

  return result
}

function useLegendData({
  metricConfigurations,
  data_id,
  comparison,
  metric1Slug,
  metric2Slug,
  interval,
  data,
  metric1Configuration,
  metric2Configuration,
  productId,
}: {
  metricConfigurations: MetricConfiguration[]
  data_id: string
  comparison: Comparison | null
  metric1Slug: string
  metric2Slug: string | null
  interval: Interval
  data: ProjectDetailsMeta
  metric1Configuration: MetricConfiguration | undefined
  metric2Configuration: MetricConfiguration | null | null | undefined
  productId?: string
}) {
  const metricsConfigurationListDic = toDictionary(
    metricConfigurations,
    "metric_id"
  )

  const {
    data: metricBreakdown,
    isFetching,
    isLoading,
  } = useMetricBreakdown({
    dataIds: [
      data_id,
      comparison?.type === "project" ? comparison.data_id : null,
    ].filter(Boolean) as string[],
    metricIds: [metric1Slug, metric2Slug]
      .filter(Boolean)
      .map((metric) => metric?.replace(/-/g, "_")) as string[],
    productIds: productId ? [productId] : undefined,
    interval,
    groupBy: "projects",
  })

  const {
    data: { data: marketSectorTotals } = { data: [] },
    isFetching: isMarketSectorTotalsFetching,
    isLoading: isMarketSectorTotalsLoading,
  } = useMetricBreakdown({
    metricIds: [metric1Slug].map((metric) => metric?.replace(/-/g, "_")),
    productIds: productId ? [productId] : undefined,
    interval,
    groupBy: "market-sector",
  })

  const marketSectorTotalsByMarketSector = marketSectorTotals
    .filter((data) => !data.data_id && data?.market_sector_id !== "")
    .reduce((acc, curr) => {
      return {
        ...acc,
        [curr.market_sector_id!]: Object.keys(curr.metrics).reduce(
          (acc, key) => {
            return {
              ...acc,
              [key]: metricsConfigurationListDic.get(key)?.static
                ? curr.metrics[key].latest
                : curr.metrics[key].sum,
            }
          },
          {}
        ),
      }
    }, {})

  const firstMarketSector = data.market_sectors[0].id

  const marketSectorTotalsByDataMarketSector =
    marketSectorTotalsByMarketSector[
      firstMarketSector as keyof typeof marketSectorTotalsByMarketSector
    ] ?? {}

  const metricBreakdownDic = toDictionary(
    metricBreakdown?.data ?? [],
    "data_id"
  )

  const metric1Key = metric1Configuration?.static ? "latest" : "sum"
  const metric1LegendValue = formatNumber({
    value:
      metricBreakdownDic.get(data.data_id)?.metrics?.[
        metric1Slug.replace(/-/g, "_")
      ]?.[metric1Key] ?? 0,
    format: "compact",
    metricFormat: metric1Configuration?.format,
  })

  const metric2Key = metric2Configuration?.static ? "latest" : "sum"
  const metric2LegendValue = metric2Slug
    ? formatNumber({
        value:
          metricBreakdownDic.get(data.data_id)?.metrics?.[
            metric2Slug.replace(/-/g, "_")
          ]?.[metric2Key] ?? 0,
        format: "compact",
        metricFormat: metric2Configuration?.format,
      })
    : null

  const projectComparisonValue =
    comparison?.type === "project"
      ? formatNumber({
          value:
            metricBreakdownDic.get(comparison.data_id)?.metrics?.[
              metric1Slug.replace(/-/g, "_")
            ]?.[metric1Key] ?? 0,
          format: "compact",
          metricFormat: metric1Configuration?.format,
        })
      : null

  const marketSectorComparisonValue =
    comparison?.type === "market_sector"
      ? formatNumber({
          value:
            marketSectorTotalsByDataMarketSector[
              metric1Slug.replace(/-/g, "_")
            ],
          format: "compact",
          metricFormat: metric1Configuration?.format,
        })
      : null

  return {
    metric1LegendValue,
    metric2LegendValue,
    projectComparisonValue,
    marketSectorComparisonValue,
    isFetching: isMarketSectorTotalsFetching || isFetching,
    isLoading: isMarketSectorTotalsLoading || isLoading,
  }
}

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const LegendItem = ({
  color,
  preLabel,
  postLabel,
  value,
  className,
  visible = true,
  tooltipMetricConfiguration,
}: {
  color: string
  preLabel: string
  postLabel: string
  value: ReactNode
  className?: string
  visible?: boolean
  tooltipMetricConfiguration?: MetricConfiguration | undefined
}) => {
  const legendContent = (
    <div className={twMerge("flex items-start gap-2", className)}>
      <div
        className="w-2 h-2 rounded-full mt-0.5 shrink-0"
        style={{
          background: color,
        }}
      />
      <div className="flex flex-col">
        {preLabel && (
          <div
            className="text-xs md:text-sm text-fg-secondary md:leading-none whitespace-nowrap truncate max-w-[200px]"
            title={preLabel}
          >
            {preLabel}
          </div>
        )}
        <div
          className={twMerge(
            "text-sm md:text-xl font-medium tracking-tight",
            visible ? "" : "invisible"
          )}
          style={{ fontFamily: "var(--font-geist-mono)" }}
        >
          {value}
        </div>
        <div
          className={twMerge(
            "text-xs text-fg-secondary leading-none mt-0.5",
            visible ? "" : "invisible"
          )}
        >
          {postLabel}
        </div>
      </div>
    </div>
  )

  if (tooltipMetricConfiguration) {
    return (
      <Tooltip
        tooltipProps={{
          placement: "bottom",
        }}
        content={
          <div className="py-3 px-2 flex flex-col gap-1">
            <p>{tooltipMetricConfiguration.title}</p>
            <p className="text-fg-secondary">
              {capitalize(tooltipMetricConfiguration.definition || "")}
            </p>
            <p className="text-fg-secondary">
              Source: {tooltipMetricConfiguration.sources}
            </p>
          </div>
        }
      >
        <Tooltip.Trigger>{legendContent}</Tooltip.Trigger>
      </Tooltip>
    )
  }

  return legendContent
}

function makeChartName({
  comparison,
  data,
  metric1Name,
  metric2Slug,
  metric2Name,
}: {
  comparison: Comparison | null
  data: ProjectDetailsMeta
  metric1Name: string
  metric2Slug: string | null
  metric2Name: string
}) {
  let chartName
  if (comparison && comparison.type === "market_sector") {
    chartName = `${data.name} vs ${comparison.label}: ${metric1Name}`
  } else if (comparison && comparison.type === "project") {
    chartName = `${data.name} vs ${comparison.label}: ${metric1Name}`
  } else if (!metric2Slug) {
    chartName = metric1Name
  } else {
    chartName = `${data.name}: ${metric1Name} vs ${metric2Name}`
  }
  return chartName
}

function setIntervalName(interval: Interval) {
  if (interval === "max") {
    return "All time"
  }

  if (interval === "ytd") {
    return "YTD"
  }

  return interval
}

const ChartLegends = ({
  showControls = true,
  metric1Configuration,
  metric2Configuration,
  metric2Slug,
  comparison,
  data,
  isLoading,
  isFetching,
  interval,
  metric1LegendValue,
  metric2LegendValue,
  projectComparisonValue,
  marketSectorComparisonValue,
  setMetric2Slug,
  setComparison,
  productId,
}: {
  showControls?: boolean
  metric1Configuration: MetricConfiguration
  metric2Configuration?: MetricConfiguration | undefined
  metric2Slug: string | null
  comparison: Comparison | null
  data: ProjectDetailsMeta
  isLoading: boolean
  isFetching: boolean
  interval: Interval
  metric1LegendValue: ReactNode
  metric2LegendValue: ReactNode | null
  projectComparisonValue: ReactNode | null
  marketSectorComparisonValue: ReactNode | null
  setMetric2Slug?: (slug: string | null) => void
  setComparison?: (comparison: Comparison | null) => void
  productId?: string
}) => {
  const { data: products = [] } = useProductsList()

  const productsDictionary = useMemo(
    () => toDictionary(products, "product_id"),
    [products]
  )
  const productName = useMemo(
    () =>
      (productId
        ? `${productsDictionary.get(productId)?.product_name} - `
        : "") || "",
    [productsDictionary, productId]
  )

  const metric1Legend = (
    <LegendItem
      className="w-full md:w-auto m-3.5 mr-5 ml-0 border border-solid border-transparent"
      color={colorsV4.chart["1"]}
      visible={!isLoading}
      preLabel={
        metric2Slug
          ? (metric1Configuration?.title ?? "")
          : `${productName} ${data.name}`
      }
      postLabel={
        metric1Configuration?.static
          ? "Latest"
          : `${setIntervalName(interval)} sum`
      }
      value={metric1LegendValue}
      tooltipMetricConfiguration={metric1Configuration}
    />
  )

  const metric2Legend = metric2Slug && (
    <LegendItem
      color={colorsV4.chart["2"]}
      preLabel={metric2Configuration?.title ?? ""}
      postLabel={
        metric2Configuration?.static
          ? "Latest"
          : `${setIntervalName(interval)} sum`
      }
      visible={!isFetching}
      value={metric2LegendValue}
      tooltipMetricConfiguration={metric2Configuration}
    />
  )

  const comparisonLegend = comparison && (
    <LegendItem
      color={
        comparison.type === "market_sector" ? "#808080" : colorsV4.chart["2"]
      }
      visible={!isFetching}
      preLabel={comparison?.label ?? ""}
      postLabel={
        metric1Configuration?.static
          ? "Latest"
          : `${setIntervalName(interval)} sum`
      }
      value={
        comparison?.type === "project"
          ? projectComparisonValue
          : marketSectorComparisonValue
      }
      tooltipMetricConfiguration={metric1Configuration}
    />
  )

  return (
    <div className="flex flex-row md:gap-3">
      {metric1Legend}
      {showControls && metric2Slug ? (
        <Button
          className="w-full md:w-auto flex items-start justify-start !p-3.5 !pr-5 h-full font-normal border border-dashed border-border-default relative"
          variant="ghost"
          onPress={() => setMetric2Slug?.(null)}
        >
          {metric2Legend}
          <Icon
            as={X}
            width={16}
            height={16}
            className="mt-0 md:-mr-1.5 text-fg-secondary md:relative absolute bottom-3 right-3 md:bottom-0 md:right-0"
          />
        </Button>
      ) : (
        metric2Legend
      )}
      {showControls && comparison ? (
        <Button
          className="w-full md:w-auto flex items-start justify-start !p-3.5 !pr-5 h-full font-normal border border-dashed border-border-default relative"
          variant="ghost"
          onPress={() => setComparison?.(null)}
          style={{
            border: "1px dashed",
            borderColor: colorsV4.border.default,
          }}
        >
          {comparisonLegend}
          <Icon
            as={X}
            width={16}
            height={16}
            className="mt-0 md:-mr-1.5 text-fg-secondary md:relative absolute bottom-3 right-3 md:bottom-0 md:right-0"
          />
        </Button>
      ) : (
        comparisonLegend
      )}
    </div>
  )
}

export const ComparisonChart = withChartContext(
  ({
    data_id,
    data,
    metric1Slug,
    headingSlot,
    granularity,
    interval,
    setGranularity,
    setInterval,
    productId,
    hideSheetsFormula,
  }: {
    data_id: string
    data: ProjectDetailsMeta
    metric1Slug: string
    granularity: GranularityType
    interval: Interval
    setGranularity?: (granularity: GranularityType) => void
    setInterval?: (interval: Interval) => void
    headingSlot?: ({
      comparison,
      title,
    }: {
      comparison: Comparison | null
      title: string
    }) => ReactNode
    productId?: string
    hideSheetsFormula?: boolean
  }) => {
    const isCopyChartImagesEnabled = useFeatureFlagEnabled("copy_chart_images")
    const studioLink = useStudioLink()
    const { data: metricConfigurations = [] } = useMetricsConfigurationList()
    const metricConfigurationsBySlug = toDictionary(
      metricConfigurations,
      "slug"
    )
    const metric1Configuration = metricConfigurationsBySlug.get(metric1Slug)
    const [showCurrentPeriod, setShowCurrentPeriod] = useState(
      metric1Configuration?.static ?? false
    )
    const [isChartImageModalOpen, setIsChartImageModalOpen] = useState(false)
    const [isProPaywallModalOpen, setIsProPaywallModalOpen] = useState(false)
    const [isFormulaModalOpen, setIsFormulaModalOpen] = useState(false)
    const [metric2Slug, setMetric2Slug] = useState<string | null>(null)
    // const [interval, setInterval] = useState<Interval>("365d")
    // const [granularity, setGranularity] = useState<GranularityType>("week")
    const [comparison, setComparison] = useState<Comparison | null>(null)

    const { data: availableMetricIdsForProject = [] } =
      useAvailableMetricIdsForProject(data.slug, productId)
    const { data: ecosystemMetricIds = [] } = useAvailableEcosystemMetricIds()
    const ecosystemMetricSlugs = ecosystemMetricIds.map((id) =>
      id.replace(/_/g, "-")
    )
    const metricConfigurationsByMetricId = toDictionary(
      metricConfigurations,
      "metric_id"
    )

    const availableMetrics: MetricConfiguration[] = useMemo(() => {
      const items = availableMetricIdsForProject
        .map((id) => metricConfigurationsByMetricId.get(id))
        .filter(Boolean) as MetricConfiguration[]

      return items
    }, [availableMetricIdsForProject, metricConfigurationsByMetricId])

    const isEcosystemMetric = useCallback(
      (metricSlug: string) => {
        return ecosystemMetricSlugs.includes(metricSlug)
      },
      [ecosystemMetricSlugs]
    )

    if (!metric1Configuration) {
      throw new Error(`Metric configuration not found for ${metric1Slug}`)
    }
    const metric1Name = metric1Configuration?.title || ""

    const metric2Configuration = useMetricsConfigurationBySlug(
      metric2Slug ?? undefined
    )
    const metric2Name = metric2Configuration?.title || ""

    const chartName = makeChartName({
      comparison,
      data,
      metric1Name,
      metric2Slug,
      metric2Name,
    })

    const {
      metric1LegendValue,
      metric2LegendValue,
      projectComparisonValue,
      marketSectorComparisonValue,
      isFetching,
      isLoading,
    } = useLegendData({
      metricConfigurations,
      data_id,
      comparison,
      metric1Slug,
      metric2Slug,
      interval,
      productId,
      data,
      metric1Configuration,
      metric2Configuration,
    })

    const chartConfig = useChartConfig({
      metric2Slug,
      comparison,
      metric1Name,
      metric1Slug,
      data_id,
      isEcosystemMetric,
      interval,
      metric2Name,
      chartName,
      granularity,
      showCurrentPeriod,
      productId,
      metric1Configuration,
    })

    const chartConfigAtom = atom(chartConfig)

    const { downloadChart } = useChartDownloadAction({
      setIsProPaywallModalOpen,
      title: chartConfig.title,
      filters: {
        project_id: data_id,
        metric_1_id: metric1Slug,
        compared_metric_id: metric2Slug,
        comparison_type: comparison?.type,
        comparison_id:
          comparison?.type === "project"
            ? comparison.project_id
            : comparison?.id,
        interval,
        granularity,
      },
    })

    const copyChartAsImageContents = (projectLogo?: ReactNode) => (
      <>
        <div className="flex flex-col md:flex-row justify-between">
          <div className="flex flex-row items-center gap-3">
            {projectLogo}
            <div className="text-lg font-medium tracking-tight leading-none">
              {chartConfig.title}
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-shrink-0 md:flex-row gap-1 md:gap-3 mt-1 -mb-4 overflow-x-auto md:w-full w-[calc(100vw-theme(spacing.9))] px-6 md:px-0 -ml-6 md:ml-0">
          <ChartLegends
            showControls={false}
            metric1Configuration={metric1Configuration}
            metric2Configuration={metric2Configuration}
            metric2Slug={metric2Slug}
            comparison={comparison}
            data={data}
            isLoading={isLoading}
            isFetching={isFetching}
            interval={interval}
            metric1LegendValue={metric1LegendValue}
            metric2LegendValue={metric2LegendValue}
            projectComparisonValue={projectComparisonValue}
            marketSectorComparisonValue={marketSectorComparisonValue}
            productId={productId}
          />
        </div>
        <ClientOnlySuspense
          fallback={
            <div className={`h-[250px] flex items-center justify-center`}>
              <LoadingSpinner diameter={24} />
            </div>
          }
        >
          <div className={`w-full h-full mt-8`}>
            <Chart
              configAtom={chartConfigAtom}
              showTitle={false}
              showDescription={false}
              showLegend={false}
              isVisible
              hideYAxisLabels={!metric2Slug}
              headingImageSlot={projectLogo}
            />
          </div>
        </ClientOnlySuspense>
      </>
    )

    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col md:flex-row justify-between">
          <div className="flex flex-row items-center gap-3">
            <div>{headingSlot?.({ comparison, title: chartConfig.title })}</div>
          </div>
          <div className="hidden md:flex flex-row items-end gap-px">
            {setInterval && setGranularity && (
              <>
                <IntervalSelectField
                  selectedKey={interval}
                  setInterval={setInterval}
                  setGranularity={setGranularity}
                  buttonProps={{
                    variant: "ghost",
                  }}
                />
                <GranularitySelectField
                  selectedKey={granularity}
                  interval={interval}
                  setGranularity={setGranularity}
                  buttonProps={{
                    variant: "ghost",
                  }}
                />
              </>
            )}
            <MenuButton
              placement="bottom end"
              trigger={
                <IconButton icon={Gear} label="Actions" variant="ghost" />
              }
            >
              <MenuButton.Item
                onAction={() => studioLink(chartConfig)}
                icon={PenNib}
              >
                Open in studio
              </MenuButton.Item>
              {!hideSheetsFormula && (
                <MenuButton.Item
                  icon={Function}
                  onAction={() => setIsFormulaModalOpen(true)}
                  // Ecosystem metrics are not supported in Sheets currently
                  isDisabled={
                    comparison?.type === "market_sector" ||
                    ecosystemMetricIds.includes(
                      metric1Slug.replace(/-/g, "_")
                    ) ||
                    ecosystemMetricIds.includes(
                      metric2Slug?.replace(/-/g, "_") || ""
                    )
                  }
                >
                  Sheets formula
                </MenuButton.Item>
              )}
              <MenuButton.Item icon={Download} onAction={downloadChart}>
                Download as CSV
              </MenuButton.Item>

              {isCopyChartImagesEnabled && (
                <MenuButton.Item
                  icon={Copy}
                  onAction={() => setIsChartImageModalOpen(true)}
                >
                  Copy as image
                </MenuButton.Item>
              )}

              <MenuButton.Separator />
              <MenuButton.Section>
                <MenuButton.Item
                  onAction={() => setShowCurrentPeriod(!showCurrentPeriod)}
                  closeOnSelect={false}
                >
                  <div className="flex flex-row items-center gap-2 pr-1">
                    <Checkbox
                      isSelected={showCurrentPeriod}
                      size="small"
                      aria-label="Show current period"
                      className="cursor-pointer"
                    />
                    Show current period
                  </div>
                </MenuButton.Item>
              </MenuButton.Section>
            </MenuButton>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-1 md:gap-3 mt-1 -mb-4 overflow-x-auto md:w-full w-[calc(100vw-theme(spacing.9))] px-6 md:px-0 -ml-6 md:ml-0">
          <ChartLegends
            metric1Configuration={metric1Configuration}
            metric2Configuration={metric2Configuration}
            metric2Slug={metric2Slug}
            comparison={comparison}
            data={data}
            isLoading={isLoading}
            isFetching={isFetching}
            interval={interval}
            metric1LegendValue={metric1LegendValue}
            metric2LegendValue={metric2LegendValue}
            projectComparisonValue={projectComparisonValue}
            marketSectorComparisonValue={marketSectorComparisonValue}
            setMetric2Slug={setMetric2Slug}
            setComparison={setComparison}
            productId={productId}
          />
          {!productId && (
            <div className="flex flex-row gap-3 md:mt-0">
              {!metric2Slug && !comparison && (
                <>
                  <MetricsDropdown
                    availableMetrics={availableMetrics}
                    selectedMetric={metric2Slug || undefined}
                    onMetricChange={setMetric2Slug}
                    trigger={
                      !metric2Slug ? (
                        <Button
                          className="w-auto flex justify-start items-start flex-col gap-2 !px-3.5 !py-3 !pr-4 h-full min-w-[120px] text-fg-secondary border border-dashed border-border-default font-normal"
                          variant="ghost"
                        >
                          <span className="text-xs md:text-sm">Add metric</span>
                          <div className="w-3 h-3 flex items-center justify-center rounded-full">
                            <Icon as={Plus} width={20} height={20} />
                          </div>
                        </Button>
                      ) : null
                    }
                    buttonProps={{
                      size: "small",
                    }}
                    allowEmpty
                  />
                  <ComparisonButton
                    comparison={comparison}
                    setComparison={setComparison}
                    data_id={data_id}
                    marketSector={data.market_sectors[0]}
                    metric={metric1Slug}
                    trigger={
                      <Button
                        className="w-auto flex justify-start items-start flex-col gap-2 !px-3.5 !py-3 !pr-4 h-full min-w-[120px] text-fg-secondary border border-dashed border-border-default font-normal"
                        variant="ghost"
                      >
                        <span className="text-xs md:text-sm">Compare with</span>
                        <div className="w-3 h-3 flex items-center justify-center rounded-full">
                          <Icon as={MagnifyingGlass} width={20} height={20} />
                        </div>
                      </Button>
                    }
                  />
                </>
              )}
            </div>
          )}
        </div>
        <ClientOnlySuspense
          fallback={
            <div className={`h-[250px] flex items-center justify-center`}>
              <LoadingSpinner diameter={24} />
            </div>
          }
        >
          <div className={`w-full h-[250px] mt-8`}>
            <Chart
              configAtom={chartConfigAtom}
              showTitle={false}
              showDescription={false}
              showLegend={false}
              isVisible
              hideYAxisLabels={!metric2Slug}
            />
          </div>
        </ClientOnlySuspense>
        <LazyProPaywallModal
          type="download_chart"
          featureName="Downloading chart data"
          isOpen={isProPaywallModalOpen}
          setIsOpen={setIsProPaywallModalOpen}
          onClose={() => setIsProPaywallModalOpen(false)}
        />
        <LazyChartFormulaModal
          isOpen={isFormulaModalOpen}
          setIsOpen={setIsFormulaModalOpen}
          onClose={() => setIsFormulaModalOpen(false)}
          series={useMemo(() => {
            const projects = [
              data_id,
              comparison?.type === "project" ? comparison.project_id : null,
            ].filter(Boolean) as string[]

            return [
              {
                metrics: [metric1Slug, metric2Slug].filter(Boolean) as string[],
                projects,
              },
            ]
          }, [metric1Slug, metric2Slug, data_id, comparison])}
          zoom={convertApiToZoom(interval)}
        />
        <CopyChartAsImageModal
          isOpen={isChartImageModalOpen}
          setIsOpen={setIsChartImageModalOpen}
          chartConfig={chartConfig}
          defaultProject={{
            project_id: data.slug.replace(/-/g, "_"),
            name: data.name,
            logo: data.logo,
          }}
          renderContent={({ projectLogo }) =>
            copyChartAsImageContents(projectLogo)
          }
        />
      </div>
    )
  }
)
